import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { HelpCircle, Search, X } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "./ui/accordion";
import { useApp } from "../context/AppContext";
import { t } from "../lib/i18n";

// Five most-asked questions before people connect a bank. Keep in this order
// (safety first, then price, then Caesar, then coverage, then language).
const QUESTIONS = ["q1", "q2", "q3", "q4", "q5"];

const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/\p{Diacritic}/gu, "");

const FAQ = () => {
    const { language } = useApp();
    const [query, setQuery] = useState("");

    // Filter questions whose Q or A text contains the (accent-insensitive) query.
    const filtered = useMemo(() => {
        const q = norm(query.trim());
        if (!q) return QUESTIONS;
        return QUESTIONS.filter((qk) => {
            const question = norm(t(language, `faq_${qk}`));
            const answer = norm(t(language, `faq_a${qk.slice(1)}`));
            return question.includes(q) || answer.includes(q);
        });
    }, [query, language]);

    // If a search narrows to a single match, auto-open it.
    const activeDefault = query.trim() && filtered.length === 1 ? filtered[0] : undefined;

    return (
        <section
            id="faq"
            className="w-full py-16 md:py-24 bg-[hsl(var(--background))] border-t border-[hsl(var(--border))]"
            data-testid="landing-faq"
        >
            <div className="max-w-4xl mx-auto px-5 sm:px-8 md:px-12">
                {/* Head */}
                <div className="text-center mb-8 md:mb-10">
                    <div className="text-[10px] md:text-xs font-bold tracking-[0.28em] text-[hsl(var(--muted-foreground))] uppercase">
                        {t(language, "faq_eyebrow")}
                    </div>
                    <h2 className="mt-3 font-display font-extrabold text-[hsl(var(--primary))] text-3xl md:text-5xl lg:text-6xl tracking-[-0.02em] leading-[1.05]">
                        {t(language, "faq_title")}
                    </h2>
                </div>

                {/* Search input */}
                <div className="relative mb-6 md:mb-8">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))] pointer-events-none" />
                    <input
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder={t(language, "faq_search_placeholder")}
                        className="w-full h-12 md:h-14 pl-11 pr-11 rounded-full bg-[hsl(var(--card))] border border-[hsl(var(--border))] focus:border-[hsl(var(--primary))]/50 focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]/20 text-sm md:text-base text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] transition-colors"
                        data-testid="faq-search-input"
                        aria-label={t(language, "faq_search_placeholder")}
                    />
                    {query && (
                        <button
                            onClick={() => setQuery("")}
                            className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 grid place-items-center rounded-full text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--secondary))]"
                            data-testid="faq-search-clear"
                            aria-label="clear"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    )}
                </div>

                {filtered.length === 0 ? (
                    <div
                        className="rounded-2xl border border-dashed border-[hsl(var(--border))] bg-[hsl(var(--card))] px-6 py-10 text-center text-sm text-[hsl(var(--muted-foreground))]"
                        data-testid="faq-no-results"
                    >
                        {t(language, "faq_no_results")}
                    </div>
                ) : (
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.5, ease: "easeOut" }}
                    >
                        <Accordion
                            key={`acc-${query}`}
                            type="single"
                            collapsible
                            defaultValue={activeDefault}
                            className="w-full space-y-3"
                        >
                            {filtered.map((qk, i) => (
                                <AccordionItem
                                    key={qk}
                                    value={qk}
                                    className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-5 md:px-6 data-[state=open]:border-[hsl(var(--primary))]/40 data-[state=open]:shadow-lg transition-all"
                                    data-testid={`faq-item-${QUESTIONS.indexOf(qk) + 1}`}
                                >
                                    <AccordionTrigger className="hover:no-underline py-5 text-left">
                                        <div className="flex items-start gap-3 pr-4 flex-1">
                                            <div className="w-7 h-7 rounded-lg bg-[hsl(var(--secondary))] grid place-items-center shrink-0 mt-0.5">
                                                <HelpCircle className="w-4 h-4 text-[hsl(var(--primary))]" />
                                            </div>
                                            <span className="font-display font-semibold text-[15px] md:text-lg text-[hsl(var(--foreground))] tracking-tight">
                                                {t(language, `faq_${qk}`)}
                                            </span>
                                        </div>
                                    </AccordionTrigger>
                                    <AccordionContent className="text-sm md:text-[15px] leading-relaxed text-[hsl(var(--muted-foreground))] pl-10 pb-5">
                                        {t(language, `faq_a${qk.slice(1)}`)}
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </motion.div>
                )}
            </div>
        </section>
    );
};

export default FAQ;

